import { Link } from '@tanstack/react-router'
import { Moon, Sun } from 'lucide-react'
import { useDays, useMeta, useSettings, useTasks } from '@/db/hooks'
import { META } from '@/db/schema'
import { db } from '@/runtime'
import { useNow } from '@/hooks/useNow'
import { clock, dayOf, hasPassed, markDay, morningLine, nightOf, nowIso, recapHeadline } from '@/lib/day'
import { todayLocal } from '@/lib/time'
import { Button } from '@/components/ui'

/**
 * The two cards that book-end Today: the morning brief from its push time
 * until noon, the recap from its push time until midnight. Each carries the
 * one button of its moment ("I'm up", "Lights out") so the habit is a tap on
 * the page you already have open. "Later" hides a card for the day.
 */
/** Whether a bookend card wants the slot at the top of Today right now. */
export const useDayCardShowing = (): boolean => {
  const settings = useSettings()
  const now = useNow(30_000)
  const today = todayLocal()
  const hour = new Date(now).getHours()
  const dismissed = useMeta<string>(META.dayCardDismissed) ?? ''
  const morning = hasPassed(settings.daily_push, now) && hour < 12 && !dismissed.includes(`morning:${today}`)
  const recap = hasPassed(settings.recap_push, now) && !dismissed.includes(`recap:${today}`)
  return morning || recap
}

export const DayCards = () => {
  const settings = useSettings()
  const now = useNow(30_000)
  const tasks = useTasks() ?? []
  const days = useDays()
  const today = todayLocal()
  const hour = new Date(now).getHours()
  const dismissed = useMeta<string>(META.dayCardDismissed) ?? ''
  const summary = dayOf(tasks, today)
  const todayRec = days.find((d) => d.spec.date === today)
  const night = days.find((d) => d.spec.date === nightOf(now))
  const showMorning =
    hasPassed(settings.daily_push, now) && hour < 12 && !dismissed.includes(`morning:${today}`)
  const showRecap = hasPassed(settings.recap_push, now) && !dismissed.includes(`recap:${today}`)
  const later = (which: 'morning' | 'recap') => void db.setMeta(META.dayCardDismissed, `${which}:${today}`)
  // one slot, not a stack: whichever bookend is the one for right now. The
  // recap wins in the evening because it is the one you can act on.
  if (!showMorning && !showRecap) return null
  return (
    <>
      {showMorning && !showRecap && (
        <DayCard
          icon={<Sun size={16} aria-hidden />}
          eyebrow="Morning brief"
          line={
            morningLine(
              summary.left.map((t) => t.spec.title),
              settings.today_cap,
            ).body
          }
          to="/morning"
          action={
            todayRec?.spec.woke_at ? (
              <span className="text-xs text-muted">Up at {clock(todayRec.spec.woke_at)}</span>
            ) : (
              <Button
                variant="chip"
                onClick={() => void markDay(today, { woke_at: nowIso() })}
                data-testid="day-wake"
              >
                I’m up
              </Button>
            )
          }
          onLater={() => later('morning')}
          testid="morning-card"
        />
      )}
      {showRecap && (
        <DayCard
          icon={<Moon size={16} aria-hidden />}
          eyebrow="Your recap is ready"
          line={
            recapHeadline(
              summary.done.length,
              summary.left.map((t) => t.spec.title),
            ).title
          }
          to="/recap"
          action={
            night?.spec.slept_at ? (
              <span className="text-xs text-muted">Lights out at {clock(night.spec.slept_at)}</span>
            ) : (
              <Button
                variant="chip"
                onClick={() => void markDay(nightOf(Date.now()), { slept_at: nowIso() })}
                data-testid="day-sleep"
              >
                Lights out
              </Button>
            )
          }
          onLater={() => later('recap')}
          testid="recap-card"
        />
      )}
    </>
  )
}

const DayCard = ({
  icon,
  eyebrow,
  line,
  to,
  action,
  onLater,
  testid,
}: {
  icon: React.ReactNode
  eyebrow: string
  line: string
  to: '/morning' | '/recap'
  action: React.ReactNode
  onLater: () => void
  testid: string
}) => (
  <div className="card px-3.5 py-3 mb-3" data-testid={testid}>
    <Link to={to} className="block">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-accent-text mb-1">
        {icon}
        {eyebrow}
      </div>
      <p className="text-[15px] leading-snug">{line}</p>
    </Link>
    <div className="mt-2 flex items-center justify-between gap-2">
      {action}
      {/* two buttons that read as two, never as one phrase ("Open later") */}
      <div className="flex items-center gap-2">
        <Link
          to={to}
          className="rounded-full bg-raised border border-line px-3 min-h-8 inline-flex items-center text-xs text-fg-2 hover:text-fg"
        >
          Open
        </Link>
        <button
          type="button"
          onClick={onLater}
          className="rounded-full px-3 min-h-8 text-xs text-muted hover:text-fg"
          data-testid={`${testid}-later`}
        >
          Later
        </button>
      </div>
    </div>
  </div>
)
